import { useState } from "react";
import { IconEye, IconEyeOff, IconSparkles } from "@tabler/icons-react";
import { cn } from "@/lib/utils/cn";
import { PasswordStrength, generateStrongPassword } from "./PasswordStrength";

const inputClass =
  "w-full rounded-xl border border-border bg-gray-50 px-4 py-3 text-sm outline-none transition-all focus:border-brand-500 focus:ring-4 focus:ring-brand-500/10";

interface Props {
  password: string;
  confirm: string;
  onPasswordChange: (value: string) => void;
  onConfirmChange: (value: string) => void;
}

/**
 * Senha + confirmar com medidor de força (regra simples), botão "sugerir senha
 * forte" (opcional) e revelar. Validação fica no formulário pai (mínimo 8 + match).
 */
export function PasswordFields({ password, confirm, onPasswordChange, onConfirmChange }: Props) {
  const [reveal, setReveal] = useState(false);
  const mismatch = confirm.length > 0 && confirm !== password;

  function suggest() {
    const pwd = generateStrongPassword();
    onPasswordChange(pwd);
    onConfirmChange(pwd);
    setReveal(true); // revela pra o usuário anotar a senha sugerida
  }

  return (
    <>
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-ink-muted">Senha</label>
          <button
            type="button"
            onClick={suggest}
            className="inline-flex items-center gap-1 text-xs font-semibold text-brand-600 hover:underline"
          >
            <IconSparkles size={14} aria-hidden />
            Sugerir senha forte
          </button>
        </div>
        <div className="relative">
          <input
            type={reveal ? "text" : "password"}
            value={password}
            onChange={(e) => onPasswordChange(e.target.value)}
            autoComplete="new-password"
            placeholder="Mínimo de 8 caracteres"
            required
            className={cn(inputClass, "pr-11")}
          />
          <button
            type="button"
            onClick={() => setReveal((v) => !v)}
            aria-label={reveal ? "Ocultar senha" : "Mostrar senha"}
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-ink-soft hover:text-ink"
          >
            {reveal ? <IconEyeOff size={18} /> : <IconEye size={18} />}
          </button>
        </div>
        <PasswordStrength password={password} />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-ink-muted">Confirmar senha</label>
        <input
          type={reveal ? "text" : "password"}
          value={confirm}
          onChange={(e) => onConfirmChange(e.target.value)}
          autoComplete="new-password"
          placeholder="Repita a senha"
          required
          aria-invalid={mismatch}
          className={cn(inputClass, mismatch && "border-danger focus:border-danger focus:ring-danger/10")}
        />
        {mismatch && <p className="text-[11px] font-medium text-danger">As senhas não coincidem.</p>}
      </div>
    </>
  );
}
